import React, { useEffect, useState } from 'react';
import { Shield, KeyRound, RefreshCw, Copy, Check } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { toast } from '../../hooks/use-toast';
import ConfirmDialog from '../../components/ui/confirm-dialog';
import { useConfirm } from '../../components/ui/use-confirm';

import { ssoAPI } from '../../services/api';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

const SSOPage = () => {
  const [loading, setLoading] = useState(true);
  const [config, setConfig] = useState(null);
  const [secret, setSecret] = useState('');
  const [logs, setLogs] = useState([]);
  const [summary, setSummary] = useState([]);
  const [copied, setCopied] = useState(false);

  const { confirm, dialogProps } = useConfirm();

  const fetchAll = async () => {
    try {
      setLoading(true);
      const [cfgRes, logsRes, summaryRes] = await Promise.all([
        ssoAPI.config(),
        ssoAPI.logs(50),
        ssoAPI.summary(),
      ]);

      const cfg = cfgRes.data;
      const logsJson = logsRes.data;
      const sumJson = summaryRes.data;

      setConfig(cfg);
      setLogs(Array.isArray(logsJson) ? logsJson : []);
      setSummary(Array.isArray(sumJson) ? sumJson : []);
    } catch (e) {
      toast({ variant: 'destructive', title: 'Failed to load SSO settings', description: String(e) });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAll();
  }, []);

  const updateConfig = async (patch) => {
    try {
      await ssoAPI.updateConfig(patch);
      toast({ title: 'SSO settings saved' });
      await fetchAll();
    } catch (e) {
      toast({ variant: 'destructive', title: 'Failed to save', description: String(e) });
    }
  };

  const rotateSecret = async () => {
    const ok = await confirm({
      title: 'Rotate SSO secret?',
      description: 'This will invalidate tokens signed with the previous secret. Update your external system immediately.',
      confirmText: 'Rotate Secret',
      cancelText: 'Cancel',
      destructive: true,
    });
    if (!ok) return;

    try {
      const res = await ssoAPI.rotate();
      setSecret(res.data.secret);
      toast({ title: 'Secret rotated' });
      await fetchAll();
    } catch (e) {
      toast({ variant: 'destructive', title: 'Failed to rotate secret', description: String(e) });
    }
  };

  const copySecret = async () => {
    if (!secret) return;
    await navigator.clipboard.writeText(secret);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96 text-slate-500">
        Loading SSO settings...
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-800 dark:text-slate-100 flex items-center gap-2">
            <Shield className="w-6 h-6" />
            SSO
          </h1>
          <p className="text-slate-500 dark:text-slate-400">Configure one-click login from external systems using a shared secret.</p>
        </div>
        <Button variant="outline" onClick={fetchAll}>
          <RefreshCw className="w-4 h-4 mr-2" />
          Refresh
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <KeyRound className="w-5 h-5" />
            Configuration
          </CardTitle>
          <CardDescription>
            External systems generate an HS256 JWT containing the user email. This CRM verifies the token and creates a cookie-based session.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-5">
          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium">Enable SSO</p>
              <p className="text-sm text-slate-500">If disabled, all SSO attempts will be rejected.</p>
            </div>
            <Switch checked={!!config?.enabled} onCheckedChange={(v) => updateConfig({ enabled: v })} />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Issuer (iss)</Label>
              <Input value={config?.issuer || ''} onChange={(e) => setConfig({ ...config, issuer: e.target.value })} placeholder="e.g. erpnext" />
            </div>
            <div className="space-y-2">
              <Label>Audience (aud)</Label>
              <Input value={config?.audience || ''} onChange={(e) => setConfig({ ...config, audience: e.target.value })} placeholder="e.g. crm" />
            </div>
          </div>

          <div className="flex gap-2">
            <Button onClick={() => updateConfig({ issuer: config?.issuer || null, audience: config?.audience || null })}>
              Save Issuer/Audience
            </Button>
            <Button variant="outline" onClick={rotateSecret}>
              Rotate Secret
            </Button>
          </div>

          {secret ? (
            <div className="p-3 rounded border bg-slate-50 dark:bg-slate-900 space-y-2">
              <div className="flex items-center justify-between">
                <p className="font-medium">New Secret</p>
                <Button size="sm" variant="outline" onClick={copySecret}>
                  {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                  <span className="ml-2">Copy</span>
                </Button>
              </div>
              <p className="font-mono text-xs break-all">{secret}</p>
            </div>
          ) : null}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Session expiry mode</Label>
              <Select
                value={config?.expiry_mode || 'FOLLOW_JWT'}
                onValueChange={(v) => updateConfig({ expiry_mode: v })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="FOLLOW_JWT">Follow incoming JWT exp</SelectItem>
                  <SelectItem value="CAP">Cap session duration</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Cap hours (when CAP)</Label>
              <Input
                type="number"
                min={1}
                value={config?.cap_hours ?? 8}
                onChange={(e) => setConfig({ ...config, cap_hours: Number(e.target.value) })}
              />
              <Button variant="outline" onClick={() => updateConfig({ cap_hours: Number(config?.cap_hours ?? 8) })}>
                Save cap
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      <Tabs defaultValue="howto">
        <TabsList>
          <TabsTrigger value="howto">Implementation</TabsTrigger>
          <TabsTrigger value="logs">SSO Logs</TabsTrigger>
          <TabsTrigger value="counts">Usage Summary</TabsTrigger>
        </TabsList>

        <TabsContent value="howto">
          <Card>
            <CardHeader>
              <CardTitle>How to implement SSO (any external system)</CardTitle>
              <CardDescription>Works for ERPNext or any system capable of generating JWT.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4 text-sm">
              <ol className="list-decimal pl-5 space-y-2">
                <li>Admin enables SSO and rotates a secret here.</li>
                <li>External system creates an HS256 JWT with claims: <code>email</code>, <code>exp</code>, <code>iat</code>, and (recommended) <code>iss</code>, <code>aud</code>.</li>
                <li>External system calls: <code>POST /api/sso-auth/login</code> with JSON <code>{'{ token, provider }'}</code>.</li>
                <li>CRM sets an HttpOnly cookie (<code>crm_session</code>). Then frontend can use the app normally.</li>
              </ol>

              <div className="p-3 rounded bg-slate-50 dark:bg-slate-900 border">
                <p className="font-medium mb-2">Example request</p>
                <pre className="text-xs whitespace-pre-wrap">{`POST ${API_BASE}/api/sso-auth/login\nContent-Type: application/json\n\n{\n  "token": "<jwt>",\n  "provider": "erpnext"\n}`}</pre>
              </div>

              <div className="p-3 rounded bg-slate-50 dark:bg-slate-900 border">
                <p className="font-medium mb-2">Example JWT claims</p>
                <pre className="text-xs whitespace-pre-wrap">{`{\n  "email": "employee@company.com",\n  "iss": "erpnext",\n  "aud": "crm",\n  "iat": 1760000000,\n  "exp": 1760003600,\n  "jti": "unique-id"\n}`}</pre>
              </div>

              <p className="text-slate-500">If the email is not present in CRM, SSO is rejected with: “User not found. Contact admin.”</p>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="logs">
          <Card>
            <CardHeader>
              <CardTitle>Recent SSO attempts</CardTitle>
              <CardDescription>Success/failure with IP and user agent.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-2">
              {logs.length === 0 ? (
                <p className="text-slate-500">No logs yet.</p>
              ) : (
                <div className="space-y-2">
                  {logs.map((l) => (
                    <div key={l.id} className="p-3 rounded border bg-white dark:bg-slate-900">
                      <div className="flex items-center justify-between">
                        <p className="font-medium text-sm">{l.user_id || 'Unknown user'} • {l.success ? 'SUCCESS' : 'FAIL'}</p>
                        <p className="text-xs text-slate-500">{l.created_at}</p>
                      </div>
                      <p className="text-xs text-slate-500">IP: {l.ip_address || '-'} • Provider: {l.sso_provider || '-'}</p>
                      {!l.success && l.failure_reason ? (
                        <p className="text-xs text-red-600 mt-1">{l.failure_reason}</p>
                      ) : null}
                      {l.user_agent ? (
                        <p className="text-xs text-slate-500 mt-1 break-words">{l.user_agent}</p>
                      ) : null}
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="counts">
          <Card>
            <CardHeader>
              <CardTitle>Usage summary</CardTitle>
              <CardDescription>How many times each user successfully used SSO.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-2">
              {summary.length === 0 ? (
                <p className="text-slate-500">No data yet.</p>
              ) : (
                <div className="space-y-2">
                  {summary.map((s) => (
                    <div key={s.user_id} className="flex items-center justify-between p-3 rounded border bg-white dark:bg-slate-900">
                      <p className="text-sm font-medium">{s.user_id}</p>
                      <p className="text-sm text-slate-600">{s.count}</p>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      <ConfirmDialog
        open={dialogProps.open}
        onOpenChange={dialogProps.onOpenChange}
        title={dialogProps.title}
        description={dialogProps.description}
        confirmText={dialogProps.confirmText}
        cancelText={dialogProps.cancelText}
        destructive={dialogProps.destructive}
        onConfirm={dialogProps.onConfirm}
        onCancel={dialogProps.onCancel}
      />
    </div>
  );
};

export default SSOPage;
